import React, { useEffect, useState } from 'react';
import {
  getEligibleStewards,
  addSteward,
  changeSteward,
  removeSteward,
} from '../../services/api';

const fullName = (p) => `${p.first_name} ${p.last_name}`.trim();
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * Add / change / remove a Primary or Backup steward.
 * mode: 'add' | 'change' | 'remove'. designation is required for change and remove;
 * role ('PRIMARY' | 'BACKUP') is required for add.
 * onDone(payload) receives the refreshed stewardship payload (with letters_updated).
 */
export default function StewardDialog({ mode, role: addRole, designation, stewardship, onClose, onDone }) {
  const [eligible, setEligible] = useState(null);
  const [personId, setPersonId] = useState('');
  const [applyToUnassigned, setApplyToUnassigned] = useState(true);
  const [moveExisting, setMoveExisting] = useState(true);
  const [reassign, setReassign] = useState('none');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const role = designation?.role || addRole;
  const roleName = role === 'BACKUP' ? 'Backup' : 'Primary';
  const unassigned = (role === 'BACKUP'
    ? stewardship?.letters_without_backup
    : stewardship?.letters_without_steward) || 0;
  const withCurrent = designation?.letter_count || 0;
  const currentId = designation?.person_id;

  useEffect(() => {
    let cancelled = false;
    getEligibleStewards()
      .then((list) => {
        if (!cancelled) setEligible(list);
      })
      .catch((e) => {
        if (!cancelled) {
          setEligible([]);
          setError(e.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [saving, onClose]);

  // People who can be picked: not already a steward of mine
  const taken = new Set((stewardship?.designations || []).map((d) => d.person_id));
  const choices = (eligible || []).filter((p) => !taken.has(p.person_id));

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      let data;
      if (mode === 'add') {
        data = await addSteward(personId, role, applyToUnassigned);
      } else if (mode === 'change') {
        data = await changeSteward(designation.id, personId, applyToUnassigned, moveExisting);
      } else {
        data = await removeSteward(designation.id, withCurrent > 0 ? reassign : undefined);
      }
      onDone(data);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const titles = {
    add: `Choose a ${roleName} steward`,
    change: `Change your ${roleName} steward`,
    remove: `Remove ${designation ? fullName(designation) : 'steward'}`,
  };
  const confirmLabels = { add: 'Add steward', change: 'Change steward', remove: 'Remove steward' };
  const needsPerson = mode !== 'remove';
  const canSubmit = !saving && (!needsPerson || personId);

  const picker = (
    <label className="stg-modal-field">
      <span>Family member</span>
      <select
        className="stg-select"
        value={personId}
        onChange={(e) => setPersonId(e.target.value)}
        disabled={eligible === null || saving}
      >
        <option value="">{eligible === null ? 'Loading...' : 'Choose a family member'}</option>
        {choices.map((p) => (
          <option key={p.person_id} value={p.person_id}>
            {fullName(p)}
          </option>
        ))}
      </select>
      {eligible !== null && choices.length === 0 && (
        <small>
          No one is available. Stewards must be adult family members with an account.
        </small>
      )}
    </label>
  );

  return (
    <div className="stg-modal-backdrop" onClick={() => !saving && onClose()}>
      <div
        className="stg-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="stg-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="stg-modal-title">{titles[mode]}</h3>

        {mode === 'add' && (
          <>
            <p className="stg-modal-text">
              {role === 'BACKUP'
                ? 'Your Backup steward can act only when your Primary steward cannot (deceased, unable to act, or none set). They cannot read or edit your letters.'
                : 'Your steward can manage delivery of your letters but cannot read or edit them.'}
            </p>
            {picker}
            {unassigned > 0 && (
              <label className="stg-modal-check">
                <input
                  type="checkbox"
                  checked={applyToUnassigned}
                  onChange={(e) => setApplyToUnassigned(e.target.checked)}
                  disabled={saving}
                />
                <span>
                  Also make them the {roleName} steward of my {plural(unassigned, 'waiting letter', 'waiting letters')} with no {roleName} steward
                </span>
              </label>
            )}
          </>
        )}

        {mode === 'change' && (
          <>
            <p className="stg-modal-text">
              Currently <strong>{fullName(designation)}</strong>
              {withCurrent > 0 && ` (${roleName} steward for ${plural(withCurrent, 'letter', 'letters')})`}.
            </p>
            {picker}
            {withCurrent > 0 && (
              <label className="stg-modal-check">
                <input
                  type="checkbox"
                  checked={moveExisting}
                  onChange={(e) => setMoveExisting(e.target.checked)}
                  disabled={saving}
                />
                <span>
                  Move the {plural(withCurrent, 'letter', 'letters')} {fullName(designation)} stewards to the new person
                </span>
              </label>
            )}
            {unassigned > 0 && (
              <label className="stg-modal-check">
                <input
                  type="checkbox"
                  checked={applyToUnassigned}
                  onChange={(e) => setApplyToUnassigned(e.target.checked)}
                  disabled={saving}
                />
                <span>
                  Also use them for my {plural(unassigned, 'waiting letter', 'waiting letters')} with no {roleName} steward
                </span>
              </label>
            )}
          </>
        )}

        {mode === 'remove' && (
          <>
            {withCurrent > 0 ? (
              <>
                <p className="stg-modal-text">
                  {fullName(designation)} is the {roleName} steward for {plural(withCurrent, 'waiting letter', 'waiting letters')}.
                  What should happen to {withCurrent === 1 ? 'it' : 'them'}?
                </p>
                <label className="stg-modal-field">
                  <select
                    className="stg-select"
                    value={reassign}
                    onChange={(e) => setReassign(e.target.value)}
                    disabled={eligible === null || saving}
                  >
                    <option value="none">Leave with no {roleName} steward</option>
                    {(eligible || [])
                      .filter((p) => p.person_id !== currentId)
                      .map((p) => (
                        <option key={p.person_id} value={p.person_id}>
                          Move to {fullName(p)}
                        </option>
                      ))}
                  </select>
                </label>
              </>
            ) : (
              <p className="stg-modal-text">
                {fullName(designation)} will no longer be your default {roleName} steward for new letters.
              </p>
            )}
          </>
        )}

        {error && (
          <div className="stg-modal-error" role="alert">
            {error}
          </div>
        )}

        <div className="stg-modal-actions">
          <button className="stg-btn stg-btn-outline" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className={`stg-btn ${mode === 'remove' ? 'stg-btn-danger' : 'stg-btn-primary'}`}
            onClick={submit}
            disabled={!canSubmit}
          >
            {saving ? 'Saving...' : confirmLabels[mode]}
          </button>
        </div>
      </div>
    </div>
  );
}
